import React from 'react';
import {
  Activity,
  Cpu,
  HardDrive,
  Image,
  Volume2,
  Triangle,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLivePreviewStore } from '../../store/livePreviewStore';

export const StatsPanel: React.FC = () => {
  const { stats, playState } = useLivePreviewStore();

  const formatMemory = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const StatItem: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: string | number;
    subValue?: string;
    color?: string;
  }> = ({ icon, label, value, subValue, color }) => (
    <div className="flex items-center gap-3 p-2 bg-zinc-800 rounded">
      <div className={cn('p-2 rounded', color || 'bg-zinc-700')}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-xs text-zinc-400">{label}</div>
        <div className="font-medium">{value}</div>
        {subValue && (
          <div className="text-xs text-zinc-500">{subValue}</div>
        )}
      </div>
    </div>
  );

  const isActive = playState !== 'stopped';

  return (
    <div className="p-3 space-y-2 max-h-96 overflow-y-auto">
      <h4 className="text-sm font-medium text-zinc-400 mb-3">Performance Stats</h4>

      <div className="grid grid-cols-2 gap-2">
        <StatItem
          icon={<Activity size={16} />}
          label="FPS"
          value={isActive ? stats.fps : '-'}
          subValue={isActive ? `${stats.frameTime.toFixed(2)} ms/frame` : undefined}
          color={
            isActive
              ? stats.fps >= 55
                ? 'bg-green-600/20 text-green-400'
                : stats.fps >= 30
                ? 'bg-yellow-600/20 text-yellow-400'
                : 'bg-red-600/20 text-red-400'
              : undefined
          }
        />

        <StatItem
          icon={<Cpu size={16} />}
          label="Draw Calls"
          value={isActive ? stats.drawCalls : '-'}
          subValue={isActive ? `${stats.triangles} triangles` : undefined}
        />

        <StatItem
          icon={<Triangle size={16} />}
          label="Objects"
          value={isActive ? `${stats.visibleObjects}/${stats.activeObjects}` : '-'}
          subValue={isActive ? 'visible/total' : undefined}
        />

        <StatItem
          icon={<HardDrive size={16} />}
          label="Memory"
          value={isActive ? formatMemory(stats.memoryUsed) : '-'}
          subValue={isActive ? `of ${formatMemory(stats.memoryTotal)}` : undefined}
        />

        <StatItem
          icon={<Image size={16} />}
          label="Textures"
          value={isActive ? stats.texturesLoaded : '-'}
          subValue={isActive ? formatMemory(stats.textureMemory) : undefined}
        />

        <StatItem
          icon={<Volume2 size={16} />}
          label="Audio"
          value={isActive ? `${stats.audioPlaying}/${stats.audioLoaded}` : '-'}
          subValue={isActive ? 'playing/loaded' : undefined}
        />
      </div>

      {/* Frame Time Graph (placeholder) */}
      <div className="mt-4">
        <div className="text-xs text-zinc-400 mb-2">Frame Time History</div>
        <div className="h-16 bg-zinc-800 rounded flex items-end gap-px px-1 py-1">
          {Array.from({ length: 60 }).map((_, i) => {
            const height = isActive
              ? Math.random() * 100
              : 0;
            return (
              <div
                key={i}
                className={cn(
                  'flex-1 rounded-t',
                  height > 66 ? 'bg-red-500' :
                  height > 33 ? 'bg-yellow-500' :
                  'bg-green-500'
                )}
                style={{ height: `${height}%` }}
              />
            );
          })}
        </div>
        <div className="flex justify-between text-xs text-zinc-500 mt-1">
          <span>0ms</span>
          <span>16.67ms</span>
          <span>33.33ms</span>
        </div>
      </div>
    </div>
  );
};
